/* ============================================================
   A foto que veio de outro app

   Segurar a foto no WhatsApp, tocar em compartilhar e escolher o Nhomi.
   Do lado de fora isso é um toque; por dentro são três peças que não se
   falam, e esta é a do meio:

     1. o ANDROID entrega a foto ao app. O que o faz aparecer na lista de
        compartilhamento é o `shareTarget` em `android/twa-manifest.json`,
        que vira um `intent-filter` no pacote — e portanto só existe a
        partir do pacote 7 na loja, não com o site publicado;
     2. o CHROME transforma o intent num POST de verdade para
        `/compartilhar`, com a imagem em `multipart/form-data`. Quem
        atende esse POST é o service worker, porque servidor nosso não
        há: a foto vai do aparelho pro aparelho e não tem por que dar uma
        volta pela internet. Ele guarda os bytes num cache e responde um
        303, que troca o POST por um GET;
     3. a TELA `/compartilhar` nasce desse GET, lê o cache aqui, pergunta
        em qual desafio e entrega as fotos para o `Compose`.

   Este arquivo é a passagem do cache para o React, e o motivo de ele
   existir separado é a travessia do passo 2 para o 3: entre o POST e a
   tela existe uma NAVEGAÇÃO INTEIRA. Nada em memória sobrevive a ela —
   nem `postMessage`, porque no instante do POST quase nunca há janela
   aberta (compartilhar é justamente o caminho de quem não abriu o app).

   O `sessionStorage` também não serve: não guarda binário sem passar por
   base64, que é 33% maior e ainda precisa ser decodificado. O cache
   guarda `Response`, que é o que o worker já tem na mão.
   ============================================================ */

/* Os três nomes são os mesmos em `public/firebase-messaging-sw.js`, e
   estão repetidos lá porque o worker não passa pelo bundler e não pode
   importar daqui. Mudar um lado sem o outro faz a foto chegar e a tela
   não achar — sem erro nenhum, só uma tela vazia. */
const COMPARTILHADO = "nhomi-compartilhado-v1";
const PASTA_COMPARTILHADA = "/__compartilhado/";

/** Onde o compartilhamento cai. Também está nos dois manifestos. */
export const TELA_COMPARTILHAR = "/compartilhar";

/**
 * Tira do cache o que o worker guardou.
 *
 * ESVAZIA ao ler, de propósito: a foto já está em memória, e deixá-la no
 * cache faria ela voltar sozinha no próximo compartilhamento que falhasse
 * — ou pior, grudar num prato que a pessoa foi publicar pela câmera dias
 * depois.
 */
async function lerDoCache(): Promise<File[]> {
  if (!("caches" in globalThis)) return [];
  try {
    const cache = await caches.open(COMPARTILHADO);
    const chaves = (await cache.keys())
      .filter((r) => new URL(r.url).pathname.startsWith(PASTA_COMPARTILHADA))
      // O worker numera com zero à esquerda pra que a ordem alfabética
      // seja a ordem em que a pessoa escolheu. A primeira é a capa.
      .sort((a, b) => a.url.localeCompare(b.url));

    const fotos: File[] = [];
    for (const chave of chaves) {
      const guardada = await cache.match(chave);
      await cache.delete(chave);
      if (!guardada) continue;
      const bytes = await guardada.blob();
      if (!bytes.size) continue;
      const nome = decodeURIComponent(guardada.headers.get("x-nome") ?? "")
        || "compartilhada.jpg";
      fotos.push(new File([bytes], nome, { type: bytes.type || "image/jpeg" }));
    }
    return fotos;
  } catch (err) {
    // Sem cache não há compartilhamento, e não há o que fazer do lado de
    // cá. O log existe porque a tela vai dizer só "não achei a foto".
    console.warn("[nhomi] não consegui ler o compartilhamento", err);
    return [];
  }
}

/* A leitura é memorizada por CARREGAMENTO DE PÁGINA porque ela destrói o
   que leu. O `StrictMode` monta todo componente duas vezes em
   desenvolvimento: sem isto, a segunda montagem encontraria a pasta
   vazia e a tela diria "não achei a foto" numa foto que chegou. */
let leitura: Promise<File[]> | null = null;

/** O que chegou pelo compartilhamento, lido uma vez só por abertura. */
export function fotosCompartilhadas(): Promise<File[]> {
  leitura ??= lerDoCache();
  return leitura;
}

/* ---------- da tela de escolha para o Compose ----------

   Daqui em diante é a mesma página: `/compartilhar` navega para
   `/c/:cid/novo` sem recarregar nada, então um módulo em memória basta e
   é o mais barato que existe.

   Não vai pelo `state` da navegação porque ele passa pelo
   `history.pushState`, que serializa: um `File` de 4 MB seria copiado
   pro histórico do navegador a cada escolha de desafio. */
let entregues: File[] = [];

/** Guarda as fotos para o `Compose` que está prestes a montar. */
export function entregarFotos(fotos: File[]): void {
  entregues = fotos;
}

/** Tem foto esperando? Só olha — dá pra chamar durante a renderização. */
export function temFotosEntregues(): boolean {
  return entregues.length > 0;
}

/** Pega as fotos e esvazia. A segunda chamada devolve lista vazia. */
export function pegarFotosEntregues(): File[] {
  const fotos = entregues;
  entregues = [];
  return fotos;
}
